import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';

import BoltIcon from '@mui/icons-material/Bolt';
import './styles.scss';

// ── Data definitions ──────────────────────────────────────────────────────────

const DASI_DATA = {
  totalRecoverable: 512450.00,
  eligibleEntries: 142,
  eligibleBrokers: 3,
  opportunities: [
    {
      id: 1,
      rootCause: 'IEEPA SCOTUS Invalidation Overpayment',
      subtext: '2025–2026 Historical Recovery Pool',
      entries: 84,
      lines: 412,
      amount: 412050.00,
    },
    {
      id: 2,
      rootCause: 'Special Program Indicator (SPI) Omission',
      subtext: 'Corporate Pivot Instructed USMCA Eligibility',
      entries: 43,
      lines: 98,
      amount: 85400.00,
    },
    {
      id: 3,
      rootCause: 'MPF Cap Violation',
      subtext: 'Broker overpaid 0.3464% statutory maximum',
      entries: 15,
      lines: 15,
      amount: 15000.00,
    },
  ],
};

const YAMAHA_DATA = {
  totalRecoverable: 388750.00,
  eligibleEntries: 97,
  eligibleBrokers: 2,
  opportunities: [
    {
      id: 1,
      rootCause: 'CIF Incoterm Freight / Insurance Non-Deduction',
      subtext: 'Entered value included non-dutiable freight across 6 suppliers',
      entries: 51,
      lines: 289,
      amount: 271500.00,
    },
    {
      id: 2,
      rootCause: 'HTS Classification Error (Chapter 87 → 8714)',
      subtext: 'AI NLP mismatch detected vs. 3rd-party broker filings',
      entries: 33,
      lines: 76,
      amount: 98250.00,
    },
    {
      id: 3,
      rootCause: 'Country of Origin Keying Error (JP → CN)',
      subtext: 'Erroneous Section 301 tariff overpayment on Japan-origin parts',
      entries: 13,
      lines: 13,
      amount: 19000.00,
    },
  ],
};

const formatCurrency = (num) =>
  '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Component ─────────────────────────────────────────────────────────────────

const TariffRecovery = () => {
  const other = useSelector((state) => state.other);
  const isYamaha = other?.selectedCompany?.Id === '1';
  const data = isYamaha ? YAMAHA_DATA : DASI_DATA;

  const [loggedItems, setLoggedItems] = useState({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [batchToastOpen, setBatchToastOpen] = useState(false);

  const handleLogToPSC = (id, rootCause) => {
    setLoggedItems((prev) => ({ ...prev, [id]: true }));
    setToastMessage(`"${rootCause}" logged to PSC queue.`);
    setToastOpen(true);
  };

  const handleBatchGenerate = () => {
    const allLogged = {};
    data.opportunities.forEach((o) => { allLogged[o.id] = true; });
    setLoggedItems(allLogged);
    setBatchToastOpen(true);
  };

  return (
    <Box className="trc-wrapper">

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <Box className="trc-kpi-grid">
        <Box className="trc-kpi-card">
          <Typography className="trc-kpi-label">Total Recoverable Capital</Typography>
          <Typography className="trc-kpi-value trc-kpi-value--green">
            {formatCurrency(data.totalRecoverable)}
          </Typography>
        </Box>

        <Box className="trc-kpi-card">
          <Typography className="trc-kpi-label">Entries Eligible for PSC</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', mt: '8px' }}>
            <Typography className="trc-kpi-value trc-kpi-value--dark">
              {data.eligibleEntries}
            </Typography>
            <Typography className="trc-kpi-subdesc">
              Across {data.eligibleBrokers} Brokers
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Opportunities Table ───────────────────────────────────────────── */}
      <Box className="trc-table-card">
        <Box className="trc-table-header">
          <Typography className="trc-table-title">Aggregated Recovery Opportunities</Typography>
          <Button
            variant="contained"
            startIcon={<BoltIcon />}
            onClick={handleBatchGenerate}
            sx={{
              backgroundColor: '#1d4ed8',
              '&:hover': { backgroundColor: '#1e40af' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
            }}
          >
            Generate Batch PSC Payload
          </Button>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table className="trc-table">
            <TableHead>
              <TableRow className="trc-thead-row">
                <TableCell className="trc-th">Root Cause (Validation Output)</TableCell>
                <TableCell className="trc-th">Affected Entries</TableCell>
                <TableCell className="trc-th">Lines</TableCell>
                <TableCell className="trc-th trc-th--right">Recoverable Amount</TableCell>
                <TableCell className="trc-th trc-th--center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.opportunities.map((opp) => {
                const isLogged = !!loggedItems[opp.id];
                return (
                  <TableRow key={opp.id} className="trc-tbody-row">
                    <TableCell className="trc-td">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box className={`trc-dot ${isLogged ? 'trc-dot--blue' : 'trc-dot--green'}`} />
                        <Box>
                          <Typography className="trc-root-cause">{opp.rootCause}</Typography>
                          <Typography className="trc-root-subtext">{opp.subtext}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell className="trc-td">{opp.entries}</TableCell>
                    <TableCell className="trc-td">{opp.lines}</TableCell>
                    <TableCell className="trc-td trc-td--right trc-amount">
                      {formatCurrency(opp.amount)}
                    </TableCell>
                    <TableCell className="trc-td trc-td--center">
                      {isLogged ? (
                        <Chip
                          label="Logged to PSC"
                          size="small"
                          sx={{
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            fontWeight: 600,
                            fontSize: '12px',
                          }}
                        />
                      ) : (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleLogToPSC(opp.id, opp.rootCause)}
                          sx={{
                            color: '#2563eb',
                            fontWeight: 600,
                            fontSize: '13px',
                            textTransform: 'none',
                            '&:hover': { color: '#1e40af' },
                          }}
                        >
                          Log to PSC →
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* ── Toasts ───────────────────────────────────────────────────────── */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={batchToastOpen}
        autoHideDuration={3500}
        onClose={() => setBatchToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setBatchToastOpen(false)} severity="success" sx={{ width: '100%' }}>
          Batch PSC payload generated for all {data.opportunities.length} recovery opportunities.
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TariffRecovery;
